import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Stack,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  DeleteForever,
  PersonOff,
  Person,
  Search,
  Clear,
  ChevronLeft,
  ChevronRight,
  FilterAltOff,
} from '@mui/icons-material';
import { FiX } from 'react-icons/fi';
import { authAPI } from '../services/api';
import TableLoader from '../components/TableLoader';
import { useMultiSelect } from '../hooks/useMultiSelect';
import BulkActionToolbar from '../components/BulkActionToolbar';
import { useAuth } from '../context/AuthContext';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { showSuccess, showError } from '../utils/toast';
import { getSortIcon } from '../utils/sortIcon';
import { colors } from '../theme/modernTheme';

export default function Users() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [userToPermanentDelete, setUserToPermanentDelete] = useState(null);
  const [confirmationName, setConfirmationName] = useState('');

  // Temporary filter states (not applied until user clicks Apply)
  const [searchInput, setSearchInput] = useState('');
  const [roleInput, setRoleInput] = useState('');
  const [statusInput, setStatusInput] = useState('');

  // Applied filter states (used for API calls)
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Sorting states
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (roleFilter) {
        params.role = roleFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await authAPI.getAllUsers(params);
      setUsers(response.data.users || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0,
      }));
    } catch (error) {
      setUsers([]);
      const errorMessage = error.error?.message || error.message || 'Failed to load users';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, roleFilter, statusFilter, pagination.page, pagination.limit, sortBy, sortOrder]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAdd = () => {
    navigate('/users/new');
  };

  const handleEdit = (user) => {
    navigate(`/users/${user.id}?mode=edit`);
  };

  const handlePermanentDeleteClick = (user) => {
    setUserToPermanentDelete(user);
    setConfirmationName('');
    setPermanentDeleteDialogOpen(true);
  };

  const handlePermanentDeleteConfirm = async () => {
    if (confirmationName !== userToPermanentDelete.name) {
      showError("Please enter the user's name exactly to confirm deletion");
      return;
    }

    try {
      await authAPI.permanentDeleteUser(userToPermanentDelete.id);
      setPermanentDeleteDialogOpen(false);
      setUserToPermanentDelete(null);
      setConfirmationName('');
      showSuccess(`User ${userToPermanentDelete.name} permanently deleted`);
      loadUsers();
    } catch (error) {
      const errorMessage =
        error.error?.message || error.message || 'Failed to permanently delete user';
      showError(errorMessage);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await authAPI.updateUser(user.id, {
        is_active: !user.is_active,
      });
      showSuccess(
        `User ${user.name} ${!user.is_active ? 'activated' : 'deactivated'} successfully`,
      );
      loadUsers();
    } catch (error) {
      const errorMessage = error.error?.message || error.message || 'Failed to update user status';
      showError(errorMessage);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleApplyFilters = () => {
    setSearchTerm(searchInput);
    setRoleFilter(roleInput);
    setStatusFilter(statusInput);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setRoleInput('');
    setStatusInput('');
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const hasActiveFilters = searchInput || roleInput || statusInput;

  // Check if filters have changed from applied state
  const hasFilterChanges =
    searchInput !== searchTerm || roleInput !== roleFilter || statusInput !== statusFilter;

  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'error';
      case 'STAFF':
        return 'primary';
      case 'READ_ONLY':
        return 'default';
      default:
        return 'default';
    }
  };

  const isAdmin = currentUser?.role === 'ADMIN';

  return (
    <Box>
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Users
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              backgroundColor: colors.primary.main,
              '&:hover': { backgroundColor: colors.primary.dark },
              borderRadius: '4px',
            }}
          >
            Add User
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '4px', boxShadow: 'none' }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Bar */}
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              InputProps={{
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput('');
                        setSearchTerm('');
                        setPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      sx={{ padding: '4px' }}
                    >
                      <FiX size={16} style={{ color: colors.text.secondary }} />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  <Search style={{ marginRight: 8, color: colors.text.secondary }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.background.paper,
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>

          {/* Filters */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={roleInput}
                label="Role"
                onChange={(e) => setRoleInput(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    '& .MuiMenu-list': {
                      padding: '0px',
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    p: '2px 8px',
                    minHeight: '32px',
                    '&:hover': { borderRadius: 0 },
                    '&.Mui-selected': { borderRadius: 0 },
                  }}
                >
                  All
                </MenuItem>
                <MenuItem
                  value="ADMIN"
                  sx={{
                    p: '2px 8px',
                    minHeight: '32px',
                    '&:hover': { borderRadius: 0 },
                    '&.Mui-selected': { borderRadius: 0 },
                  }}
                >
                  Admin
                </MenuItem>
                <MenuItem
                  value="STAFF"
                  sx={{
                    p: '2px 8px',
                    minHeight: '32px',
                    '&:hover': { borderRadius: 0 },
                    '&.Mui-selected': { borderRadius: 0 },
                  }}
                >
                  Staff
                </MenuItem>
                <MenuItem
                  value="READ_ONLY"
                  sx={{
                    p: '2px 8px',
                    minHeight: '32px',
                    '&:hover': { borderRadius: 0 },
                    '&.Mui-selected': { borderRadius: 0 },
                  }}
                >
                  Read Only
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusInput}
                label="Status"
                onChange={(e) => setStatusInput(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    '& .MuiMenu-list': {
                      padding: '0px',
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    p: '2px 8px',
                    minHeight: '32px',
                    '&:hover': { borderRadius: 0 },
                    '&.Mui-selected': { borderRadius: 0 },
                  }}
                >
                  All
                </MenuItem>
                <MenuItem
                  value="active"
                  sx={{
                    p: '2px 8px',
                    minHeight: '32px',
                    '&:hover': { borderRadius: 0 },
                    '&.Mui-selected': { borderRadius: 0 },
                  }}
                >
                  Active
                </MenuItem>
                <MenuItem
                  value="false"
                  sx={{
                    p: '2px 8px',
                    minHeight: '32px',
                    '&:hover': { borderRadius: 0 },
                    '&.Mui-selected': { borderRadius: 0 },
                  }}
                >
                  Inactive
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                disableRipple
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  mr: -1,
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Apply
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear
              </IconButton>
            </Box>
          </Grid>

          {/* Pagination */}
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <IconButton
                size="small"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: Math.max(1, prev.page - 1),
                  }))
                }
                disabled={pagination.page === 1}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                Page {pagination.page} of {pagination.pages || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: Math.min(pagination.pages, prev.page + 1),
                  }))
                }
                disabled={pagination.page >= pagination.pages}
                sx={{
                  borderRadius: 1,
                  marginLeft: -2,
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <TableLoader message="Loading users..." />
      ) : users.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: colors.text.secondary,
          }}
        >
          <Typography variant="body1">No users found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: `1px solid ${colors.border.main}`,
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <Table>
            <TableHead sx={{ borderBottom: '1px solid #E5E7EB' }}>
              <TableRow>
                <TableCell
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: colors.text.primary,
                  }}
                  onClick={() => handleSort('name')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    User
                    {getSortIcon(sortBy, sortOrder, 'name')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: colors.text.primary,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Role
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: colors.text.primary,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Status
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: colors.text.primary,
                  }}
                  onClick={() => handleSort('last_login_at')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Last Login
                    {getSortIcon(sortBy, sortOrder, 'last_login_at')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: colors.text.primary,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  onClick={() => navigate(`/users/${user.id}`)}
                  sx={{
                    cursor: 'pointer',
                    opacity: !user.is_active ? 0.7 : 1,
                    '&:hover': { backgroundColor: colors.action.row },
                    '&:not(:last-of-type)': {
                      borderBottom: `1px solid ${colors.border.main}`,
                    },
                  }}
                >
                  <TableCell sx={{ maxWidth: '250px', py: 1 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          background: user.is_active ? colors.primary.main : colors.text.disabled,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                        }}
                      >
                        {user.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()}
                      </Avatar>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: colors.text.primary,
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Tooltip title={user.name} arrow placement="top">
                            <span>{user.name}</span>
                          </Tooltip>
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.text.secondary,
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Tooltip title={user.email} arrow placement="top">
                            <span>{user.email}</span>
                          </Tooltip>
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '100px', py: 1 }}>
                    <Chip
                      label={user.role}
                      size="small"
                      sx={{
                        backgroundColor:
                          getRoleColor(user.role) === 'error'
                            ? `${colors.error.main}15`
                            : getRoleColor(user.role) === 'primary'
                              ? `${colors.primary.main}15`
                              : `${colors.success.main}15`,
                        color:
                          getRoleColor(user.role) === 'error'
                            ? colors.error.main
                            : getRoleColor(user.role) === 'primary'
                              ? colors.primary.main
                              : colors.secondary.main,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: '4px',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '100px', py: 1 }}>
                    <Chip
                      label={user.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        backgroundColor: user.is_active
                          ? `${colors.success.main}15`
                          : colors.action.hover,
                        color: user.is_active ? colors.success.main : colors.text.secondary,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: '4px',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '120px', py: 1 }}>
                    <Typography
                      sx={{
                        color: colors.text.secondary,
                        fontSize: '0.875rem',
                      }}
                    >
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ py: 1 }}>
                    <Box display="flex" gap="4px">
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(user)}
                          disabled={!isAdmin && currentUser?.id !== user.id}
                          sx={{
                            color: colors.text.primary,
                            p: 1,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: colors.primary.main,
                            },
                          }}
                        >
                          <Edit sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <>
                          <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(user)}
                              disabled={currentUser?.id === user.id}
                              sx={{
                                color: colors.text.primary,
                                p: 1,
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: user.is_active ? colors.error.main : colors.success.main,
                                },
                              }}
                            >
                              {user.is_active ? (
                                <PersonOff sx={{ fontSize: 16 }} />
                              ) : (
                                <Person sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          {isAdmin && currentUser?.id !== user.id && (
                            <Tooltip title="Permanently Delete">
                              <IconButton
                                size="small"
                                onClick={() => handlePermanentDeleteClick(user)}
                                sx={{
                                  color: colors.text.primary,
                                  p: 1,
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: colors.error.main,
                                  },
                                }}
                              >
                                <DeleteForever sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && users.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() =>
              setPagination((prev) => ({
                ...prev,
                page: Math.max(1, prev.page - 1),
              }))
            }
            disabled={pagination.page === 1}
            sx={{
              marginRight: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            Page {pagination.page} of {pagination.pages || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() =>
              setPagination((prev) => ({
                ...prev,
                page: Math.min(pagination.pages, prev.page + 1),
              }))
            }
            disabled={pagination.page >= pagination.pages}
            sx={{
              marginLeft: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Permanent Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={permanentDeleteDialogOpen}
        onClose={() => {
          setPermanentDeleteDialogOpen(false);
          setConfirmationName('');
        }}
        onConfirm={handlePermanentDeleteConfirm}
        title="Permanently Delete User?"
      >
        <Typography variant="body2" sx={{ color: colors.error.main, fontWeight: 700, mb: 1.5 }}>
          WARNING: THIS ACTION IS IRREVERSIBLE
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
          Permanently deleting <strong>{userToPermanentDelete?.name}</strong> will:
        </Typography>
        <Box
          component="ul"
          sx={{
            textAlign: 'left',
            display: 'inline-block',
            mx: 'auto',
            mb: 2,
            px: 2,
            fontSize: '0.85rem',
            color: colors.text.secondary,
          }}
        >
          <li>Remove all user data from the system</li>
          <li>Invalidate all active sessions</li>
          <li>Delete all associated records</li>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: colors.text.primary,
            mb: 1,
            fontWeight: 600,
          }}
        >
          Type "{userToPermanentDelete?.name}" to confirm:
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Type name here"
          value={confirmationName}
          onChange={(e) => setConfirmationName(e.target.value)}
          error={confirmationName && confirmationName !== userToPermanentDelete?.name}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              backgroundColor: colors.background.light,
            },
          }}
        />
      </DeleteConfirmationDialog>
    </Box>
  );
}
